import { assertDataIsNotResponse } from '@n2-common/asserts';
import { getApplicationIds } from '@n2-common/components/ApplicationContext';
import { N2ApplicationStatus } from '@n2-common/serverGeneratedTypings/n2ApplicationStatus';
import { N2MajorApplicationStatus } from '@n2-common/serverGeneratedTypings/n2MajorApplicationStatus';
import { generatePath, LoaderFunctionArgs, NavigateFunction, redirect, useLoaderData } from 'react-router-dom';
import { ApplicationStatusModel } from '../serverGeneratedTypings/applicationStatusModel';
import { getApplicationStatus } from '../services/legacyApplicationApiService';
import { navigateToStep, ApplicationPaths } from './ApplicationFormsHelpers';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';
import { ProductTypeForTracking } from '@n2-common/lib/gtm';
import { DocumentRequirement } from '@n2-common/components/UploadDocuments';
import { ActionState } from '@n2-common/ui/ActionList';

export const translationNamepaceFromProduct = (productType: ProductTypeForTracking) => {
    switch (productType) {
        case 'SA':
            return 'deposit';
        case 'CC':
            return 'cc';
        default:
            return 'common';
    }
};

export const getDocumentationState = (requirements: DocumentRequirement[]): ActionState => {
    if (requirements === null) {
        return 'pending';
    }
    const pendingDocuments = requirements.filter((m) => !m.completed);
    return pendingDocuments.length > 0 ? 'ready' : 'completed';
};

export const fetchApplicationStatusAndRedirectIfNotExpectedState = async (
    applicationId: string | null,
    applicantId: string,
    correctStateFunc: (applicationStatus: ApplicationStatusModel) => boolean,
    setApplicationStatus: (applicationStatus: ApplicationStatusModel) => void,
    navigate: NavigateFunction,
    navigateToCorrectStepFunc: (application: ApplicationStatusModel, applicantId: string, navigate: NavigateFunction) => void
) => {
    if (!applicationId) {
        console.log('no applicationId');
        return;
    }
    const res = await getApplicationStatus(applicationId, applicantId);
    if (!correctStateFunc(res)) {
        navigateToCorrectStepFunc(res, applicantId, navigate);
    }
    setApplicationStatus(res);
};

export const commonNavigateToCorrectStepForSingleApplicantProducts = (
    application: ApplicationStatusModel,
    applicantId: string,
    navigate: NavigateFunction
) => {
    const target = __navigateTargetFromStatus(application, applicantId);
    if (target) {
        navigateToStep(target.path, target.applicationId, navigate, target.applicantId);
    }
    return;
};

// Internal function not intended to be used outside module. Only exported, to allow unittest.
export function __navigateTargetFromStatus(application: ApplicationStatusModel, applicantId: string) {
    if (application.Status === N2ApplicationStatus.Offer) {
        return { path: ApplicationPaths.offer, applicationId: application.ApplicationId, applicantId };
    }

    if (application.Status === N2ApplicationStatus.Completed) {
        return { path: ApplicationPaths.completed, applicationId: application.ApplicationId, applicantId };
    }

    if (application.Status === N2ApplicationStatus.InProgress) {
        return { path: ApplicationPaths.inProgress, applicationId: application.ApplicationId, applicantId };
    }

    switch (application.MajorStatus) {
        case N2MajorApplicationStatus.Declined:
            return {
                path: application.IsAgentApplication ? ApplicationPaths.declinedFromAgent : ApplicationPaths.declined,
                applicationId: application.ApplicationId,
                applicantId,
            };

        case N2MajorApplicationStatus.Cancelled:
            return { path: ApplicationPaths.canceled, applicationId: application.ApplicationId, applicantId };

        case N2MajorApplicationStatus.NotFound:
            return { path: ApplicationPaths.notFound, applicationId: application.ApplicationId, applicantId };
    }

    if (application.Status === N2ApplicationStatus.ReadyForSigning) {
        if (application.ApplicantCddPending) {
            return { path: ApplicationPaths.offer, applicationId: application.ApplicationId, applicantId };
        } else {
            return { path: ApplicationPaths.sign, applicationId: application.ApplicationId, applicantId };
        }
    }
    if (application.Status === N2ApplicationStatus.InProgressCustomerFinished) {
        return { path: ApplicationPaths.applicantActionsCompleted, applicationId: application.ApplicationId, applicantId };
    }

    if (application.Status === N2ApplicationStatus.ReadyForPinSelect) {
        return { path: ApplicationPaths.sign, applicationId: application.ApplicationId, applicantId };
    }

    console.log(`dont know where to navigate to with applicationStatus ${application.MajorStatus}, ${application.Status} `);
    return undefined as never;
}

function generatePathQuery(target: ReturnType<typeof __navigateTargetFromStatus>) {
    const { path, applicantId, applicationId } = target;
    return generatePath(`${path}?applicationId=${applicationId}&applicantId=${applicantId}`);
}

export function getRedirectUrl(applicationStatus: ApplicationStatusModel, applicantId: string) {
    const target = __navigateTargetFromStatus(applicationStatus, applicantId);
    if (target) {
        return generatePathQuery(target);
    }
    return ApplicationPaths.error;
}
/**
 * Function that redirects from requested url, and have guard for redirecting to same url (causing endles redirects).
 * Including serverside logging.
 */
export function safeRedirect(requestUrl: string, status: ApplicationStatusModel, applicantId: string) {
    const redirectUrl = getRedirectUrl(status, applicantId);
    if (requestUrl.includes(redirectUrl)) {
        const report = {
            requestUrl,
            redirectUrl,
            applicationStatus: status,
        };
        logErrorToServerside(`Invalid state resulting in endless redirect`, status.ApplicationId, applicantId, report);
        return redirect(generatePath(ApplicationPaths.error));
    }
    return redirect(redirectUrl);
}

export async function applicationLoader({ request }: LoaderFunctionArgs) {
    const requestUrl = new URL(request.url);

    if (!requestUrl.searchParams.has('applicationId')) {
        return redirect(generatePath(ApplicationPaths.wizard));
    }
    try {
        const { applicantId, applicationId } = await getApplicationIds(request);

        const status = await getApplicationStatus(applicationId, applicantId);
        return redirect(getRedirectUrl(status, applicantId));
    } catch (err) {
        const applicationId = requestUrl.searchParams.get('applicationId');
        logErrorToServerside(`Could not load application`, applicationId, null, err);
        return redirect(generatePath(ApplicationPaths.error));
    }
}

export function useApplicationIdsLoaderData() {
    const data = useLoaderData() as Awaited<ReturnType<typeof applicationLoader>>;
    assertDataIsNotResponse(data);
    return data;
}
